import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, RotateCcw, Save } from "lucide-react";

const fmt = (n) => (Number(n) || 0).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 2 });

const PRESET_DEPOSITS = [10, 25, 50, 100];

const EMPTY = {
  deposit_type: "percentage",
  deposit_amount: 25,
  balance_due_type: "7_days_before",
  balance_due_days: 7,
  cancellation_type: "moderate",
  cancellation_free_until_hours: 168,
  cancellation_refund_type: "none",
  cancellation_refund_percent: 0,
  late_payment_policy: "",
  damage_policy: "",
  rescheduling_policy: "",
  weather_policy: "",
  other_terms: "",
};

export default function VendorPolicies({ vendors }) {
  const [vendorId, setVendorId] = useState(vendors?.[0]?.vendor_id || "");
  const [policy, setPolicy] = useState(EMPTY);
  const [resolved, setResolved] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewQuote, setPreviewQuote] = useState(2000);

  useEffect(() => { if (!vendorId && vendors?.length) setVendorId(vendors[0].vendor_id); }, [vendors, vendorId]);

  const load = () => {
    if (!vendorId) return;
    setLoading(true);
    api.get(`/vendors/${vendorId}/payment-policy`)
      .then(({ data }) => {
        setPolicy({
          deposit_type: data.deposit_type,
          deposit_amount: data.deposit_amount,
          balance_due_type: data.balance_due_type,
          balance_due_days: data.balance_due_days,
          cancellation_type: data.cancellation_type,
          cancellation_free_until_hours: data.cancellation_free_until_hours,
          cancellation_refund_type: data.cancellation_refund_type,
          cancellation_refund_percent: data.cancellation_refund_percent,
          late_payment_policy: data.late_payment_policy || "",
          damage_policy: data.damage_policy || "",
          rescheduling_policy: data.rescheduling_policy || "",
          weather_policy: data.weather_policy || "",
          other_terms: data.other_terms || "",
        });
        setResolved(data);
      })
      .catch(() => setResolved(null))
      .finally(() => setLoading(false));
  };
  useEffect(load, [vendorId]);

  const set = (patch) => setPolicy(p => ({ ...p, ...patch }));

  const preview = useMemo(() => {
    const q = Math.max(0, Number(previewQuote) || 0);
    let deposit = 0;
    if (policy.deposit_type === "percentage") deposit = q * (Number(policy.deposit_amount) || 0) / 100;
    else if (policy.deposit_type === "fixed") deposit = Math.min(Number(policy.deposit_amount) || 0, q);
    const balance = Math.max(0, q - deposit);
    const balanceDays = policy.balance_due_type === "custom"
      ? Math.max(0, Number(policy.balance_due_days) || 0)
      : { upon_arrival: 0, "7_days_before": 7, "14_days_before": 14, "30_days_before": 30 }[policy.balance_due_type] || 7;
    return { quote: q, deposit: +deposit.toFixed(2), balance: +balance.toFixed(2), balanceDays };
  }, [previewQuote, policy]);

  const save = async () => {
    if (!vendorId) return;
    setSaving(true);
    try {
      const { data } = await api.put(`/vendors/${vendorId}/payment-policy`, policy);
      setResolved(data);
      toast.success("Policy updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!vendorId) return;
    if (!confirm("Reset to platform defaults? Existing bookings keep their snapshot policy.")) return;
    try {
      await api.delete(`/vendors/${vendorId}/payment-policy`);
      load();
      toast.success("Reset to defaults");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Reset failed"); }
  };

  if (!vendors?.length) return <div className="text-[color:var(--text-muted)] text-sm" data-testid="policies-empty">Create a listing first to configure payment terms.</div>;

  const showFreeUntilPicker = policy.cancellation_type === "custom";
  const showRefundPercent = policy.cancellation_type === "custom" && policy.cancellation_refund_type === "custom";
  const showCustomBalanceDays = policy.balance_due_type === "custom";
  const showDepositAmount = policy.deposit_type !== "none";
  const isPercent = policy.deposit_type === "percentage";

  return (
    <div data-testid="policies-panel" className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={vendorId} onValueChange={setVendorId}>
          <SelectTrigger data-testid="policies-vendor-select" className="bg-white h-11 max-w-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{vendors.map(v => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.name}</SelectItem>)}</SelectContent>
        </Select>
        {resolved && !resolved.is_custom && (
          <span data-testid="using-default-badge" className="text-[10px] uppercase tracking-widest bg-[color:var(--surface)] text-[color:var(--text-muted)] px-2 py-1 rounded-full">
            Platform default
          </span>
        )}
        <div className="ml-auto flex gap-2">
          {resolved?.is_custom && (
            <Button variant="outline" onClick={reset} data-testid="policies-reset" className="rounded-lg h-10">
              <RotateCcw className="w-4 h-4 mr-2" /> Reset
            </Button>
          )}
          <Button onClick={save} disabled={saving || loading} data-testid="policies-save" className="btn-sage rounded-lg h-10">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save policy
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr,380px] gap-8">
        {/* Editor */}
        <div className="space-y-8">
          {/* Deposit */}
          <section className="card-elegant p-6 space-y-4" data-testid="deposit-section">
            <div className="eyebrow">Deposit</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Require deposit?</Label>
                <Select value={policy.deposit_type} onValueChange={(v) => set({ deposit_type: v })}>
                  <SelectTrigger data-testid="deposit-type-select" className="bg-white h-11 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No deposit required</SelectItem>
                    <SelectItem value="fixed">Fixed dollar amount</SelectItem>
                    <SelectItem value="percentage">Percentage of total booking</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {showDepositAmount && (
                <div>
                  <Label className="text-xs">{isPercent ? "Deposit percentage (%)" : "Deposit amount ($)"}</Label>
                  {isPercent ? (
                    <div className="mt-1 flex gap-1.5 flex-wrap">
                      {PRESET_DEPOSITS.map(v => (
                        <button
                          key={v}
                          type="button"
                          data-testid={`deposit-preset-${v}`}
                          onClick={() => set({ deposit_amount: v })}
                          className={`text-xs rounded-full px-3 py-1.5 border ${Number(policy.deposit_amount) === v ? "bg-[color:var(--accent)] text-white border-[color:var(--accent)]" : "border-[color:var(--border)] hover:border-[color:var(--border-strong)]"}`}
                        >{v}%</button>
                      ))}
                      <Input
                        type="number" min="0" max="100"
                        value={policy.deposit_amount}
                        onChange={(e) => set({ deposit_amount: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
                        className="bg-white h-9 w-24 text-sm" data-testid="deposit-amount-input"
                      />
                    </div>
                  ) : (
                    <Input
                      type="number" min="0"
                      value={policy.deposit_amount}
                      onChange={(e) => set({ deposit_amount: Math.max(0, Number(e.target.value) || 0) })}
                      className="bg-white h-11 mt-1" data-testid="deposit-amount-input"
                    />
                  )}
                </div>
              )}
            </div>
          </section>

          {/* Balance */}
          <section className="card-elegant p-6 space-y-4" data-testid="balance-section">
            <div className="eyebrow">Remaining balance</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Remaining balance due</Label>
                <Select value={policy.balance_due_type} onValueChange={(v) => set({ balance_due_type: v })}>
                  <SelectTrigger data-testid="balance-type-select" className="bg-white h-11 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="upon_arrival">Upon arrival</SelectItem>
                    <SelectItem value="7_days_before">7 days before event</SelectItem>
                    <SelectItem value="14_days_before">14 days before event</SelectItem>
                    <SelectItem value="30_days_before">30 days before event</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {showCustomBalanceDays && (
                <div>
                  <Label className="text-xs">Days before event</Label>
                  <Input
                    type="number" min="0"
                    value={policy.balance_due_days}
                    onChange={(e) => set({ balance_due_days: Math.max(0, Number(e.target.value) || 0) })}
                    className="bg-white h-11 mt-1" data-testid="balance-custom-days"
                  />
                </div>
              )}
            </div>
          </section>

          {/* Cancellation */}
          <section className="card-elegant p-6 space-y-4" data-testid="cancellation-section">
            <div className="eyebrow">Cancellation policy</div>
            <div>
              <Label className="text-xs">Choose a preset — or build your own</Label>
              <Select value={policy.cancellation_type} onValueChange={(v) => set({ cancellation_type: v })}>
                <SelectTrigger data-testid="cancellation-type-select" className="bg-white h-11 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="flexible">Flexible — free cancel until 24h before</SelectItem>
                  <SelectItem value="moderate">Moderate — free cancel up to 7 days before</SelectItem>
                  <SelectItem value="strict">Strict — deposit non-refundable after confirmation</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {showFreeUntilPicker && (
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Free cancellation until</Label>
                  <Select value={String(policy.cancellation_free_until_hours)} onValueChange={(v) => set({ cancellation_free_until_hours: Number(v) })}>
                    <SelectTrigger data-testid="cancellation-window-select" className="bg-white h-11 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="24">24 hours before</SelectItem>
                      <SelectItem value="48">48 hours before</SelectItem>
                      <SelectItem value="72">3 days before</SelectItem>
                      <SelectItem value="168">7 days before</SelectItem>
                      <SelectItem value="336">14 days before</SelectItem>
                      <SelectItem value="720">30 days before</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Refund after deadline</Label>
                  <Select value={policy.cancellation_refund_type} onValueChange={(v) => set({ cancellation_refund_type: v })}>
                    <SelectTrigger data-testid="refund-type-select" className="bg-white h-11 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No refund</SelectItem>
                      <SelectItem value="50_percent">50% refund</SelectItem>
                      <SelectItem value="deposit_only">Full deposit refund only</SelectItem>
                      <SelectItem value="custom">Custom refund percentage</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            {showRefundPercent && (
              <div>
                <Label className="text-xs">Refund percentage (%)</Label>
                <Input
                  type="number" min="0" max="100"
                  value={policy.cancellation_refund_percent}
                  onChange={(e) => set({ cancellation_refund_percent: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
                  className="bg-white h-11 mt-1 max-w-xs" data-testid="refund-percent-input"
                />
              </div>
            )}
          </section>

          {/* Additional Policies */}
          <section className="card-elegant p-6 space-y-4" data-testid="extra-policies-section">
            <div className="eyebrow">Additional policies (optional)</div>
            {[
              ["late_payment_policy", "Late payment policy"],
              ["damage_policy", "Damage policy"],
              ["rescheduling_policy", "Rescheduling policy"],
              ["weather_policy", "Weather policy"],
              ["other_terms", "Other terms & conditions"],
            ].map(([k, label]) => (
              <div key={k}>
                <Label className="text-xs">{label}</Label>
                <Textarea
                  data-testid={`policy-${k}`}
                  rows={3}
                  value={policy[k]}
                  onChange={(e) => set({ [k]: e.target.value })}
                  className="bg-white mt-1"
                  placeholder={label + " — briefly explain your policy so planners know what to expect."}
                />
              </div>
            ))}
          </section>
        </div>

        {/* Live preview */}
        <aside className="lg:sticky lg:top-24 h-fit space-y-4">
          <div className="card-elegant p-6" data-testid="policy-preview">
            <div className="eyebrow mb-4">What planners will see</div>
            <div>
              <Label className="text-xs">Preview quote total</Label>
              <Input
                type="number" min="0"
                value={previewQuote}
                onChange={(e) => setPreviewQuote(Math.max(0, Number(e.target.value) || 0))}
                className="bg-white h-11 mt-1" data-testid="preview-quote-input"
              />
            </div>
            <div className="mt-5 space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-[color:var(--text-muted)]">Booking total</span><span className="font-semibold">{fmt(preview.quote)}</span></div>
              <div className="flex justify-between">
                <span className="text-[color:var(--text-muted)]">Deposit due today</span>
                <span className="font-semibold" data-testid="preview-deposit">
                  {isPercent && policy.deposit_type === "percentage" && <span className="text-[10px] uppercase tracking-widest mr-2">{policy.deposit_amount}%</span>}
                  {fmt(preview.deposit)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-[color:var(--text-muted)]">Remaining balance</span>
                <span className="font-semibold">{fmt(preview.balance)}</span>
              </div>
              <div className="text-xs text-[color:var(--text-muted)]">
                Due {preview.balanceDays === 0 ? "upon arrival" : `${preview.balanceDays} day${preview.balanceDays === 1 ? "" : "s"} before event`}
              </div>
            </div>
            <div className="mt-5 pt-5 border-t border-[color:var(--border)]">
              <div className="eyebrow mb-2">Cancellation policy</div>
              <div className="text-xs text-[color:var(--text-muted)] leading-relaxed">
                {resolved?.computed?.cancellation_summary}
              </div>
            </div>
          </div>
          <div className="text-xs text-[color:var(--text-muted)] leading-relaxed" data-testid="policy-note">
            Changes take effect for new bookings only. Existing bookings keep the policy that was in effect when the planner confirmed.
          </div>
        </aside>
      </div>
    </div>
  );
}
